import React, { useState } from 'react'
import { Table, Tag, Input, Button, Space } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import type { ColumnType } from 'antd/es/table'
import type { FilterConfirmProps } from 'antd/es/table/interface'
import Highlighter from 'react-highlight-words'

// Assuming 'users' is the prop passed to this component containing user data
function Users({ users }: any) {
  const [searchText, setSearchText] = useState('')
  const [searchedColumn, setSearchedColumn] = useState('')

  const getSubscriptionPackageFilters = (users: any) => {
    const uniquePackages = Array.from(
      new Set((users || []).map((user: any) => user.subscription?.package || 'None'))
    )

    return uniquePackages.map((pkg: any) => ({
      text: pkg,
      value: pkg,
    }))
  }

  const handleSearch = (
    selectedKeys: string[],
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: string
  ) => {
    confirm()
    setSearchText(selectedKeys[0])
    setSearchedColumn(dataIndex)
  }

  const handleReset = (
    clearFilters: () => void,
    confirm: (param?: FilterConfirmProps) => void
  ) => {
    clearFilters()
    setSearchText('')
    confirm()
  }

  const getColumnSearchProps = (dataIndex: string): ColumnType<any> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters as () => void, confirm)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
          .toString()
          .toLowerCase()
          .includes((value as string).toLowerCase())
        : '',
    render: (text: string) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  })

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      ...getColumnSearchProps('name'),
      sorter: (a: any, b: any) => {
        const nameA = a.name || ''
        const nameB = b.name || ''
        return nameA.localeCompare(nameB)
      },
      render: (text: string, record: any) =>
        record.isAnonymous ? 'Anonymous' : text || 'No Name Provided',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      ...getColumnSearchProps('email'),
      sorter: (a: any, b: any) => {
        const emailA = a.email || ''
        const emailB = b.email || ''
        return emailA.localeCompare(emailB)
      },
      render: (text: string, record: any) =>
        record.isAnonymous ? 'Not Provided' : text || 'No Email Provided',
    },
    {
      title: 'Sex',
      dataIndex: 'sex',
      key: 'sex',
      filters: [
        { text: 'Male', value: 'male' },
        { text: 'Female', value: 'female' },
        { text: 'Not Provided', value: 'Not Provided' },
      ],
      onFilter: (value: any, record: any) =>
        (record.sex || 'Not Provided') === value,
      sorter: (a: any, b: any) => {
        const sexA = a.sex || ''
        const sexB = b.sex || ''
        return sexA.localeCompare(sexB)
      },
      render: (text: string) => text || 'Not Provided',
    },
    {
      title: 'Date of Birth',
      dataIndex: 'dateOfBirth',
      key: 'dateOfBirth',
      sorter: (a: any, b: any) => {
        const dateA = new Date(a.dateOfBirth).getTime()
        const dateB = new Date(b.dateOfBirth).getTime()
        return dateA - dateB
      },
      render: (text: string) =>
        text ? new Date(text).toLocaleDateString() : 'Not Provided',
    },
    {
      title: 'Registered At',
      dataIndex: 'creationDate',
      key: 'creationDate',
      sorter: (a: any, b: any) => {
        const dateA = new Date(a.creationDate).getTime()
        const dateB = new Date(b.creationDate).getTime()
        return dateA - dateB
      },
      render: (text: string) =>
        text ? new Date(text).toLocaleDateString() : 'Not Provided',
    },
    {
      title: 'Subscription Package',
      dataIndex: ['subscription', 'package'],
      key: 'package',
      filters: getSubscriptionPackageFilters(users || []),
      onFilter: (value: any, record: any) =>
        (record.subscription?.package || 'None') === value,
      sorter: (a: any, b: any) => {
        const packageA = a.subscription?.package || 'None'
        const packageB = b.subscription?.package || 'None'
        return packageA.localeCompare(packageB)
      },
      render: (text: string) => (
        <Tag
          color={
            text === 'MONTHLY'
              ? 'blue'
              : text === 'EXPIRED'
                ? 'volcano'
                : 'grey'
          }
        >
          {text || 'None'}
        </Tag>
      ),
    },
    {
      title: 'Subscription Status',
      key: 'isSubscribed',
      dataIndex: ['subscription', 'isSubscribed'],
      filters: [
        { text: 'Subscribed', value: true },
        { text: 'Not Subscribed', value: false },
      ],
      onFilter: (value: any, record: any) =>
        record.subscription?.isSubscribed === value,
      render: (isSubscribed: boolean) =>
        isSubscribed ? 'Subscribed' : 'Not Subscribed',
    },
    {
      title: 'Verified',
      dataIndex: 'isVerified',
      key: 'isVerified',
      sorter: (a: any, b: any) => Number(a.isVerified) - Number(b.isVerified),
      filters: [
        { text: 'Verified', value: true },
        { text: 'Unverified', value: false },
        { text: 'N/A', value: 'N/A' },
      ],
      onFilter: (value: any, record: any) => {
        if (value === 'N/A') return record.isAnonymous
        return record.isVerified === value
      },
      render: (isVerified: boolean, record: any) =>
        record.isAnonymous ? (
          <Tag color="default">N/A</Tag>
        ) : isVerified ? (
          <Tag color="green">Verified</Tag>
        ) : (
          <Tag color="red">Unverified</Tag>
        ),
    },
  ]

  return (
    <div>
      <h4>Registered Users</h4>
      <Table columns={columns} dataSource={users || []} rowKey="_id" />
    </div>
  )
}

export default Users
